import React, { FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, IJSXOption } from "@evergis/ui";

import { goToLayerConfiguration, goToEqlQuery, goToMainBase } from "../../../ducks/location/location";
import { isLocationLayerConfigurationSelector, isLocationEqlQuerySelector } from "../../../ducks/location/selectors";
import { useTempLayer } from "../../../hooks/layer/useTempLayer";
import { useProjectName } from "../../../hooks/project/useProjectName";
import { useTableLayersOptions } from "../hooks/useTableLayersOptions";
import { useTableLayerId } from "../../../hooks/table/useTableLayerId";

import { RoutePanelName } from "../../../appConfig/types";

export const TableLayersList: FC<{ currentLayer: string }> = ({ currentLayer }) => {
  const dispatch = useDispatch();
  const [, setTableLayerId] = useTableLayerId();
  const projectName = useProjectName();
  const isLayerConfiguration = useSelector(isLocationLayerConfigurationSelector);
  const isEqlQueryLocation = useSelector(isLocationEqlQuerySelector);
  const layersOptions = useTableLayersOptions();
  const { tempLayerSourceName, getTempLayerInfo } = useTempLayer();

  const options = useMemo(() => {
    if (tempLayerSourceName) {
      const tempLayerInfo = getTempLayerInfo(tempLayerSourceName);

      return layersOptions.find(({ value }) => value === tempLayerSourceName)
        ? layersOptions
        : [...layersOptions, { text: tempLayerInfo.alias || tempLayerSourceName, value: tempLayerSourceName }];
    }

    return layersOptions;
  }, [tempLayerSourceName, getTempLayerInfo, layersOptions]);

  const onChange = useCallback(
    value => {
      const layerName = value[0].value;

      if (layerName !== currentLayer) {
        if (isLayerConfiguration) {
          dispatch(goToLayerConfiguration({ layer: layerName, query: {} }));
        } else if (isEqlQueryLocation) {
          dispatch(goToEqlQuery({ layer: layerName, query: {} }));
        } else {
          dispatch(
            goToMainBase({
              project: projectName,
              panel: RoutePanelName.Table,
              layer: layerName,
              query: { layerName },
            }),
          );
        }

        setTableLayerId(layerName);
      }
    },
    [currentLayer, isLayerConfiguration, isEqlQueryLocation, setTableLayerId, dispatch, projectName],
  );

  return (
    <Dropdown
      width="300px"
      value={currentLayer}
      options={options as unknown as IJSXOption[]}
      onChange={onChange}
      zIndex={100}
    />
  );
};
